import { redirect } from 'next/navigation';
import axios from 'axios';
import Cookies from 'js-cookie';

import { i18n } from '@/i18n-config';
import { env, storage_keys } from '@/constant/configs';
import { removeCustomerAuthCreds } from '@/lib/utils/functions';
import { deleteFCMToken } from '@/lib/utils/fcm-cleanup';

const instance = axios.create({
  baseURL: env.api_url + '/v1/customer',
});

const isServer = typeof window === 'undefined';

instance.interceptors.request.use(async (config) => {
  if (isServer) {
    const { cookies } = await import('next/headers');
    const cookieStore = await cookies();

    const token = cookieStore.get(storage_keys.customer_token)?.value;

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  } else {
    const pathname = window.location.pathname;

    i18n.locales.forEach((value) => {
      if (pathname.startsWith(`/${value}`)) {
        config.headers['Accept-Language'] = value;
        return;
      }
    });

    // Set timezone header
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    config.headers['User-Timezone'] = timezone;

    const impId = Cookies.get(storage_keys.impersonate_id);
    if (impId) {
      config.headers['x-proxy-user-id'] = impId;
    }
    const customerToken = localStorage.getItem(storage_keys.customer_token);
    if (customerToken) {
      config.headers['Authorization'] = `Bearer ${customerToken}`;
    }
  }

  return config;
});

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem(
          storage_keys.customer_refresh_token
        );
        const response = await axios.post(
          env.api_url + '/v1/customer/issue-new-auth-token-set',
          null,
          { headers: { Authorization: `Bearer ${refreshToken}` } }
        );

        localStorage.setItem(storage_keys.customer_token, response.data.token);
        localStorage.setItem(
          storage_keys.customer_refresh_token,
          response.data.refresh_token
        );

        if (!isServer) {
          Cookies.set(storage_keys.customer_token, response.data.token, {
            expires: new Date(response.data.token_expires_at),
          });
        }

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
        return axios(originalRequest);
      } catch (error2) {
        // Delete FCM token on auth failure
        if (!isServer) {
          await deleteFCMToken();
        }
        removeCustomerAuthCreds();
        // If error on refresh token
        if (isServer) {
          // TODO: needs testing
          redirect('/login');
        } else {
          localStorage.removeItem(storage_keys.customer_cart);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default instance;
